import { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { Upload, UploadProps, Modal, Button } from '@ets/design-system';

import { useDispatch, useSelector } from '_common/hooks';
import { toast } from 'utils/toast';

import { closeAndResetModal } from '_common/modals/ModalsSlice';
import {
  validateUsersCSV,
  addUsersCSV,
} from 'Settings/pages/TenantSettingsPage/Users/redux/userManagementSlice';

import Loading from 'Settings/pages/TenantSettingsPage/Users/Loading/Loading';
import ErrorReport from './ErrorReport/ErrorReport';
import Information from './Information/Information';
import type { ErrorReportProps } from './ErrorReport/ErrorReport';

import style from './UploadUsersModal.module.scss';

type UploadState =
  | 'initial'
  | 'loading'
  | 'error'
  | 'uploaded_all_valid'
  | 'uploaded_some_valid'
  | 'uploaded_none_valid';

export const TEST_IDS = {
  allValid: { msg: 'settings.users.upload.allValid.msg' },
  someValid: { msg: 'settings.users.upload.someValid.msg' },
  noneValid: { msg: 'settings.users.upload.noneValid.msg' },
};

const MODAL = 'UploadUsersModal';
const UPLOAD_STATE: Record<string, UploadState> = {
  INITIAL: 'initial',
  LOADING: 'loading',
  ERROR: 'error',
  UPLOADED_ALL_VALID: 'uploaded_all_valid',
  UPLOADED_SOME_VALID: 'uploaded_some_valid',
  UPLOADED_NONE_VALID: 'uploaded_none_valid',
};

type EntriesData = {
  validEntries: Pick<ErrorReportProps['entries'][number], 'row'>[];
  invalidEntries: ErrorReportProps['entries'];
};

const UploadUsersModal = () => {
  const dispatch = useDispatch();

  // redux
  const isOpen = useSelector((state) => state.modals.open[MODAL]);

  //local
  // State to control what should be rendered accordingly to the state of the upload action
  const [currentState, setCurrentState] = useState(UPLOAD_STATE.INITIAL);
  // Uploaded file data
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  // Data related to the uploaded users' entries
  const [data, setData] = useState<EntriesData | null>(null);

  useEffect(() => {
    if (data && data?.validEntries?.length > 0) {
      if (data?.invalidEntries?.length < 1) {
        setCurrentState(UPLOAD_STATE.UPLOADED_ALL_VALID);
      } else {
        setCurrentState(UPLOAD_STATE.UPLOADED_SOME_VALID);
      }
    } else if (data && data?.invalidEntries?.length > 0) {
      setCurrentState(UPLOAD_STATE.UPLOADED_NONE_VALID);
    } else {
      setCurrentState(UPLOAD_STATE.INITIAL);
    }
  }, [data]);

  const close = () => {
    setCurrentState(UPLOAD_STATE.INITIAL);
    setSelectedFile(null);
    setData(null);
    dispatch(closeAndResetModal(MODAL));
  };

  const handleUpload: UploadProps['onDrop'] = async (acceptedFiles) => {
    setCurrentState(UPLOAD_STATE.LOADING);

    if (
      !acceptedFiles ||
      !acceptedFiles[0] ||
      (acceptedFiles[0].type && acceptedFiles[0].name.indexOf('csv')) === -1
    ) {
      setCurrentState(UPLOAD_STATE.ERROR);
      return;
    }

    // enpoint response
    try {
      const { response } = await dispatch(validateUsersCSV(acceptedFiles[0])).unwrap();
      const validEntries: EntriesData['validEntries'] = [];
      const invalidEntries: EntriesData['invalidEntries'] = [];

      if (response.data) {
        const entries = response.data;

        Object.keys(entries).forEach((row) => {
          //@ts-expect-error Missing endpoint type "/api/authority/user/validate/csv"
          const entry = entries[row];

          if (entry.valid) {
            validEntries.push({ row });
          } else {
            const errors = entry?.errors;

            if (errors) {
              invalidEntries.push({ row, errors });
            }
          }
        });

        setSelectedFile(acceptedFiles[0]);
        setData({ validEntries, invalidEntries });
      }
    } catch (e) {
      setCurrentState(UPLOAD_STATE.ERROR);
    }
  };

  // Handles the form submition
  const handleSubmit = async () => {
    if (selectedFile) {
      setCurrentState(UPLOAD_STATE.LOADING);
      const { users } = await dispatch(addUsersCSV(selectedFile)).unwrap();

      toast({
        variant: 'success',
        title: 'NEW_USERS_ADDED_TO_THE_TENANT',
        message: 'NEW_USERS_WERE_SUCCESSFULLY_ADDED',
        messageValues: {
          total: `${users.length}`,
        },
      });

      setCurrentState(UPLOAD_STATE.INITIAL);
      close();
    }
  };

  return (
    <Modal open={!!isOpen} close={close} width="75rem" testId="upload-users">
      <div className={style.form}>
        <Modal.Header>
          <FormattedMessage id="UPLOAD_USERS" />
        </Modal.Header>
        <Modal.Body>
          <div className={style.body}>
            <div className={style.content}>
              {(currentState === UPLOAD_STATE.INITIAL || currentState === UPLOAD_STATE.ERROR) && (
                <div className={style.inputUpload}>
                  <Upload
                    size="m"
                    name="upload"
                    testId="upload-input"
                    accept={{ 'text/csv': ['.csv'] }}
                    onDrop={handleUpload}
                    icon="cloud-arrow-up"
                    errorMessage={
                      currentState === UPLOAD_STATE.ERROR ? (
                        <FormattedMessage id="UPLOAD_INVALID_FILE_FORMAT" />
                      ) : undefined
                    }
                  >
                    <FormattedMessage id="DRAG_AND_DROP_CSV_USERS" />
                    <Button
                      variant="standard"
                      size="m"
                      style={{ margin: '1.5rem 2rem 0 0' }}
                      testId={`upload-input-label`}
                    >
                      <FormattedMessage id="BROWSE_YOUR_COMPUTER" />
                    </Button>
                  </Upload>
                  <Information />
                </div>
              )}
              {currentState === UPLOAD_STATE.LOADING && (
                <div className={style.loading}>
                  <Loading message={{ id: 'UPLOADING_FILE' }} icon="file" />
                </div>
              )}
              {data && currentState === UPLOAD_STATE.UPLOADED_ALL_VALID && (
                <div className={style.message} data-testid={TEST_IDS.allValid.msg}>
                  <FormattedMessage
                    id="FOUND_ALL_USERS_ENTRIES_VALID"
                    values={{ entries: data.validEntries.length }}
                  />
                </div>
              )}
              {data && currentState === UPLOAD_STATE.UPLOADED_SOME_VALID && (
                <div className={style.message}>
                  <div data-testid={TEST_IDS.someValid.msg}>
                    <FormattedMessage
                      id="FOUND_SOME_USERS_ENTRIES_VALID"
                      values={{
                        entries: data.validEntries.length + data.invalidEntries.length,
                        valid: data.validEntries.length,
                      }}
                    />
                    .
                  </div>
                  <ErrorReport entries={data.invalidEntries} />
                  <div className={style.messageFooter}>
                    <FormattedMessage id="ADD_VALID_USERS_OR_CANCEL" />
                  </div>
                </div>
              )}
              {data && currentState === UPLOAD_STATE.UPLOADED_NONE_VALID && (
                <div className={style.message}>
                  <div data-testid={TEST_IDS.noneValid.msg}>
                    <FormattedMessage
                      id="FOUND_NONE_USERS_ENTRIES_VALID"
                      values={{ entries: data.validEntries.length + data.invalidEntries.length }}
                    />
                    .
                  </div>
                  <ErrorReport entries={data.invalidEntries} />
                </div>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button size="m" variant="standard" onClick={close} testId="cancel">
            <FormattedMessage id="global.cancel" />
          </Button>
          {(currentState === UPLOAD_STATE.UPLOADED_ALL_VALID ||
            currentState === UPLOAD_STATE.UPLOADED_SOME_VALID) && (
            <Button size="m" variant="primary" onClick={handleSubmit} testId="cta">
              {currentState === UPLOAD_STATE.UPLOADED_ALL_VALID && (
                <FormattedMessage id="ADD_USERS_TO_THE_TENANT" />
              )}
              {currentState === UPLOAD_STATE.UPLOADED_SOME_VALID && (
                <FormattedMessage id="ADD_VALID_USERS_TO_THE_TENANT" />
              )}
            </Button>
          )}
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default UploadUsersModal;
